import React, { useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Chrome, Eye, EyeOff, KeyRound, Lock, Mail, Phone } from "lucide-react";
import {
  getOrCreateInvisibleRecaptcha,
  requestPhoneOtp,
  sendResetEmail,
  signInWithEmail,
  signInWithGoogle,
} from "../services/authService";
import { upsertUserProfile } from "../services/profileService";
import { PageShell } from "../components/PageShell";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const HERO_SRC = "/Images/hero-farm.jpg";

export default function LoginPage() {
  const [identifier, setIdentifier] = useState(""); // email or phone
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [resetMode, setResetMode] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const confirmationRef = useRef(null);
  const recaptchaRef = useRef(null);

  const isEmail = identifier.trim().includes("@");
  const identifierError = useMemo(() => {
    const value = identifier.trim();
    if (!value) return "";
    if (isEmail) {
      const ok = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
      return ok ? "" : "Enter a valid email address.";
    }
    if (/[a-zA-Z]/.test(value)) return "Phone mode accepts numbers only. Add @ to use email mode.";
    const parsed = formatPhoneE164(value);
    return parsed.ok ? "" : parsed.error;
  }, [identifier, isEmail]);

  async function onLogin(e) {
    e.preventDefault();
    if (!isEmail) return;
    setBusy(true);
    try {
      await signInWithEmail(identifier.trim(), password);
      toast.success("Welcome back.");
      navigate("/dashboard");
    } catch (err) {
      toast.error("Login failed", { description: err?.message || "Invalid credentials." });
    } finally {
      setBusy(false);
    }
  }

  async function onReset(e) {
    e.preventDefault();
    if (!identifier.trim() || !isEmail) {
      toast.error("Enter your email first.");
      return;
    }
    setBusy(true);
    try {
      await sendResetEmail(identifier.trim());
      toast.success("Reset email sent", { description: "Check your inbox (and spam folder)." });
    } catch (err) {
      toast.error("Reset failed", { description: err?.message || "Try again." });
    } finally {
      setBusy(false);
    }
  }

  async function onGoogle() {
    setBusy(true);
    try {
      const result = await signInWithGoogle();
      const u = result.user;
      // Ensure a profile exists for Header display name.
      const fullName = u.displayName || "";
      await upsertUserProfile(u.uid, {
        fullName,
        displayName: fullName,
        email: u.email || "",
      });
      toast.success("Signed in with Google.");
      navigate("/dashboard");
    } catch (err) {
      toast.error("Google sign-in failed", { description: err?.message || "Try again." });
    } finally {
      setBusy(false);
    }
  }

  function formatPhoneE164(raw) {
    const cleaned = String(raw || "")
      .trim()
      .replace(/[^\d+]/g, "");

    if (!cleaned) return { ok: false, error: "Enter a phone number." };

    if (cleaned.startsWith("+")) {
      if (/^\+\d{10,15}$/.test(cleaned)) return { ok: true, value: cleaned };
      return { ok: false, error: "Use international format like +91XXXXXXXXXX." };
    }

    const digits = cleaned.replace(/\D/g, "");
    // Default to India if user enters 10-digit local number.
    if (/^\d{10}$/.test(digits)) return { ok: true, value: `+91${digits}` };
    // If user types country code without + (e.g. 91xxxxxxxxxx)
    if (/^\d{11,15}$/.test(digits)) return { ok: true, value: `+${digits}` };

    return { ok: false, error: "Invalid phone number. Try +91XXXXXXXXXX." };
  }

  async function requestOtp(e) {
    e.preventDefault();
    const parsed = formatPhoneE164(identifier);
    if (!parsed.ok) return toast.error(parsed.error);
    setBusy(true);
    try {
      const verifier = recaptchaRef.current || getOrCreateInvisibleRecaptcha("recaptcha-container", "__sf_recaptcha_login");
      recaptchaRef.current = verifier;
      const result = await requestPhoneOtp(parsed.value, verifier);
      confirmationRef.current = result;
      setOtpSent(true);
      toast.success("OTP sent", { description: "Check your phone for the code." });
    } catch (err) {
      if (err?.code === "auth/billing-not-enabled") {
        toast.error("Phone login needs billing", {
          description: "Enable the Blaze plan for this Firebase project to use Phone Auth.",
        });
      } else {
        toast.error("OTP failed", { description: err?.message || "Try again." });
      }
    } finally {
      setBusy(false);
    }
  }

  async function verifyOtp(e) {
    e.preventDefault();
    if (!otpSent || !confirmationRef.current) {
      toast.error("Request OTP first.");
      return;
    }
    if (!otp.trim()) {
      toast.error("Enter the OTP code.");
      return;
    }
    setBusy(true);
    try {
      const cred = await confirmationRef.current.confirm(otp.trim());
      const u = cred.user;
      await upsertUserProfile(u.uid, {
        fullName: u.displayName || "",
        displayName: u.displayName || "",
        email: u.email || "",
        phone: u.phoneNumber || formatPhoneE164(identifier).value || "",
      });
      toast.success("Signed in.");
      navigate("/dashboard");
    } catch (err) {
      toast.error("OTP verification failed", { description: err?.message || "Try again." });
    } finally {
      setBusy(false);
    }
  }

  return (
    <PageShell>
      <section className="px-4 sm:px-6 pt-24 sm:pt-10 pb-24">
        <div className="mx-auto max-w-6xl grid items-start gap-10 lg:grid-cols-2">
          <div className="hidden overflow-hidden rounded-3xl border border-border bg-cream shadow-elevated lg:block">
            <img
              src={HERO_SRC}
              alt="Rows of crops at sunrise"
              className="h-72 w-full object-cover"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
            <div className="px-8 py-7">
              <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-4 py-1.5 text-[10px] font-medium uppercase tracking-[0.22em] text-foreground">
                <span className="h-1.5 w-1.5 rounded-full bg-accent" />
                Smart Farming
              </div>
              <h1 className="mt-4 font-display text-5xl text-foreground text-balance">
                Monitor. Automate. <span className="italic text-accent">Grow.</span>
              </h1>
              <p className="mt-3 max-w-md text-sm leading-relaxed text-foreground">
                Sign in to your dashboard to view real-time telemetry, device status, and alerts.
              </p>
            </div>
          </div>

          <div className="rounded-3xl border border-border bg-card shadow-soft p-6 sm:p-10">
            <div>
              <div className="font-display text-2xl sm:text-3xl">
                {!isEmail ? "Sign in with phone" : resetMode ? "Reset password" : "Welcome back"}
              </div>
              <p className="mt-2 text-sm text-muted-foreground">
                {!isEmail
                  ? "We’ll send a one-time code to your number."
                  : resetMode
                    ? "We’ll email you a secure reset link."
                    : "Use your email and password to continue."}
              </p>
            </div>

            <div id="recaptcha-container" style={{ position: "absolute", left: "-9999px", top: "-9999px" }} />

            <form
              className="mt-6 space-y-4"
              onSubmit={isEmail ? (resetMode ? onReset : onLogin) : (otpSent ? verifyOtp : requestOtp)}
            >
              <label className="block">
                <span className="text-xs uppercase tracking-widest text-muted-foreground">Email or phone</span>
                <div className="mt-2 flex items-center gap-2 rounded-2xl border border-input bg-cream/50 px-4 py-3">
                  {isEmail ? <Mail className="h-4 w-4 text-muted-foreground" /> : <Phone className="h-4 w-4 text-muted-foreground" />}
                  <input
                    className="w-full bg-transparent outline-none text-sm"
                    value={identifier}
                    onChange={(e) => {
                      const v = e.target.value;
                      setIdentifier(v);
                      // reset phone OTP state if the identifier changes
                      setOtpSent(false);
                      setOtp("");
                      confirmationRef.current = null;
                      if (v.includes("@")) setResetMode(false);
                    }}
                    placeholder={isEmail ? "you@example.com" : "+91XXXXXXXXXX"}
                    required
                    autoComplete={isEmail ? "email" : "tel"}
                  />
                </div>
                {identifierError ? <div className="mt-2 text-xs text-destructive">{identifierError}</div> : null}
              </label>

              {isEmail ? (
                <>
                <label className="block">
                  <span className="text-xs uppercase tracking-widest text-muted-foreground">Password</span>
                  <div className="mt-2 flex items-center gap-2 rounded-2xl border border-input bg-cream/50 px-4 py-3">
                    <Lock className="h-4 w-4 text-muted-foreground" />
                    <input
                      className="w-full bg-transparent outline-none text-sm"
                      type={showPass ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      required={!resetMode}
                      autoComplete="current-password"
                      disabled={resetMode}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPass((s) => !s)}
                      className="text-muted-foreground hover:text-foreground transition-colors"
                      aria-label={showPass ? "Hide password" : "Show password"}
                      disabled={resetMode}
                    >
                      {showPass ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </label>

                <Button size="lg" className="w-full" disabled={busy} type="submit">
                  {resetMode ? "Send reset link" : "Sign in"}
                </Button>
              </>
              ) : (
                <>
                  {otpSent && (
                    <label className="block">
                      <span className="text-xs uppercase tracking-widest text-muted-foreground">OTP</span>
                      <div className="mt-2 flex items-center gap-2 rounded-2xl border border-input bg-cream/50 px-4 py-3">
                        <KeyRound className="h-4 w-4 text-muted-foreground" />
                        <input
                          className="w-full bg-transparent outline-none text-sm tracking-widest"
                          value={otp}
                          onChange={(e) => setOtp(e.target.value)}
                          placeholder="123456"
                          inputMode="numeric"
                        />
                      </div>
                    </label>
                  )}
                  <Button size="lg" className="w-full" disabled={busy || Boolean(identifierError)} type="submit">
                    {otpSent ? "Verify OTP" : "Send OTP"}
                  </Button>
                </>
              )}

              <div className="flex flex-col gap-3 text-sm sm:flex-row sm:items-center sm:justify-between">
                {isEmail ? (
                  <button
                    type="button"
                    className="text-accent underline-grow w-fit"
                    onClick={() => setResetMode((m) => !m)}
                  >
                    {resetMode ? "Back to sign in" : "Forgot password?"}
                  </button>
                ) : (
                  <span className="text-muted-foreground">
                    {otpSent ? (
                      <button type="button" className="text-accent underline-grow" onClick={() => { setOtpSent(false); setOtp(""); }}>
                        Change number
                      </button>
                    ) : (
                      "No password needed."
                    )}
                  </span>
                )}
                <div className="text-muted-foreground">
                  New here?{" "}
                  <Link className="text-accent underline-grow" to="/signup">
                    Create an account
                  </Link>
                </div>
              </div>
            </form>

            <div className="mt-6 border-t border-border pt-6">
              <Button
                variant="outline"
                onClick={onGoogle}
                type="button"
                disabled={busy}
                className="w-full"
              >
                <Chrome className="h-4 w-4" />
                Continue with Google
              </Button>
            </div>
          </div>
        </div>
      </section>
    </PageShell>
  );
}
